import { useRef, useState } from "react";
import {
  Bot,
  ChevronDown,
  ExternalLink,
  Send,
  Sparkles,
  Wrench,
} from "lucide-react";
import { clsx } from "clsx";

import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { AronMarkdown } from "@/features/aron/components/AronMarkdown";
import { PageHeader } from "@/features/power_projects/components/ui";

import { useEzAsk } from "../hooks";
import type { AskModel, ChatMessage, ProceedingRow } from "../types";
import { DeadlinePill, TagChip, WorkModeChip, fmtPln } from "../ui";

const MODELS: { value: AskModel; label: string; hint: string }[] = [
  {
    value: "deepseek-v4-pro",
    label: "DeepSeek Pro",
    hint: "domyślny — tani i rozsądny",
  },
  { value: "deepseek-v4-flash", label: "DeepSeek Flash", hint: "najtańszy" },
  { value: "claude-haiku-4-5", label: "Claude Haiku", hint: "szybki" },
  {
    value: "claude-sonnet-4-6",
    label: "Claude Sonnet",
    hint: "najmocniejszy, najdroższy",
  },
];

const SUGGESTIONS = [
  "Znajdź otwarte przetargi na oświetlenie uliczne z terminem powyżej 14 dni",
  "Za ile ostatnio wygrywano budowy sieci elektroenergetycznych w lubuskim?",
  "Gdzie jest najmniejsza konkurencja w przetargach na PV?",
  "Które otwarte przetargi z branż priorytetowych mają najbliższe terminy?",
];

function TenderMiniCard({ row }: { row: ProceedingRow }) {
  return (
    <div className="flex items-start justify-between gap-2 rounded-lg border border-[var(--color-border)] px-2.5 py-1.5 text-xs bg-[var(--color-bg-surface)]">
      <div className="min-w-0">
        <div className="flex items-center gap-1 flex-wrap mb-0.5">
          <WorkModeChip mode={row.work_mode} />
          {row.tags.slice(0, 3).map((t) => (
            <TagChip key={t} tag={t} />
          ))}
        </div>
        <p className="leading-snug">
          {row.source_url ? (
            <a
              href={row.source_url}
              target="_blank"
              rel="noreferrer"
              className="hover:underline font-medium"
            >
              {row.title} <ExternalLink size={10} className="inline" />
            </a>
          ) : (
            <span className="font-medium">{row.title}</span>
          )}
        </p>
        <p className="text-[10px] text-[var(--color-text-muted)] truncate">
          {row.organization_name} ({row.organization_province})
          {row.price_total && <> · wygrana {fmtPln(row.price_total)}</>}
          {row.offers_count != null && <> · {row.offers_count} ofert</>}
        </p>
      </div>
      <DeadlinePill row={row} />
    </div>
  );
}

/* Collapsible trace of the model's narration + tool calls ("thinking view"). */
function StepsTrace({ msg }: { msg: ChatMessage }) {
  if (!msg.steps?.length) return null;
  const tools = msg.steps.filter((s) => s.type === "tool").length;
  return (
    <details className="mb-2 group">
      <summary className="cursor-pointer select-none text-[11px] text-[var(--color-text-muted)] inline-flex items-center gap-1 hover:text-[var(--color-text-secondary)]">
        <ChevronDown
          size={12}
          className="transition-transform group-open:rotate-180"
        />
        Przebieg analizy — {tools} {tools === 1 ? "narzędzie" : "narzędzia"},{" "}
        {msg.steps.length} kroków
      </summary>
      <div className="mt-1.5 space-y-1 border-l-2 border-rose-200 dark:border-rose-800 pl-2.5">
        {msg.steps.map((s, i) => (
          <p
            key={i}
            className={clsx(
              "text-[11px] leading-snug",
              s.type === "tool"
                ? "text-rose-700 dark:text-rose-400 font-medium inline-flex items-start gap-1"
                : "text-[var(--color-text-muted)] italic",
            )}
          >
            {s.type === "tool" && (
              <Wrench size={11} className="mt-0.5 shrink-0" />
            )}
            <span className="break-all">{s.text}</span>
          </p>
        ))}
      </div>
    </details>
  );
}

export default function EzChatPage() {
  const ask = useEzAsk();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [model, setModel] = useState<AskModel>("deepseek-v4-pro");
  const bottomRef = useRef<HTMLDivElement>(null);

  const totalCost = messages.reduce(
    (acc, m) => acc + (m.cost_usd ? Number(m.cost_usd) : 0),
    0,
  );

  const scroll = () =>
    setTimeout(
      () => bottomRef.current?.scrollIntoView({ behavior: "smooth" }),
      60,
    );

  const send = (question: string) => {
    const q = question.trim();
    if (!q || ask.isPending) return;
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((prev) => [...prev, { role: "user", content: q }]);
    setDraft("");
    scroll();
    ask.mutate(
      { question: q, history, model },
      {
        onSuccess: (r) => {
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content: r.answer,
              tenders: r.tenders,
              steps: r.steps,
              cost_usd: r.cost_usd,
              model: r.model,
            },
          ]);
          scroll();
        },
        onError: () => {
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content: "Nie udało się uzyskać odpowiedzi — spróbuj ponownie.",
            },
          ]);
          scroll();
        },
      },
    );
  };

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto flex flex-col min-h-[calc(100vh-4rem)]">
      <PageHeader
        title="Czat EZ-ARON"
        subtitle="Smart search i analiza językiem naturalnym nad bazą BZP."
        backTo="/ezamowienia"
        backLabel="Oferty przetargowe"
        actions={
          <div className="flex items-center gap-3">
            {totalCost > 0 && (
              <span
                className="inline-flex items-center gap-1 text-[11px] font-medium rounded-full px-2 py-1 bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:ring-rose-800 tabular-nums"
                title="Łączny koszt AI tej rozmowy"
              >
                <Sparkles size={11} /> rozmowa: ${totalCost.toFixed(4)}
              </span>
            )}
            <select
              value={model}
              onChange={(e) => setModel(e.target.value as AskModel)}
              className="text-xs border border-[var(--color-border)] rounded-lg px-2.5 py-1.5 bg-transparent"
              title={MODELS.find((m) => m.value === model)?.hint}
            >
              {MODELS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label} — {m.hint}
                </option>
              ))}
            </select>
          </div>
        }
      />

      <div className="flex-1 space-y-3">
        {messages.length === 0 && (
          <div className="rounded-xl border border-dashed border-[var(--color-border)] p-6 text-center">
            <Bot size={28} className="mx-auto text-rose-500 mb-2" />
            <p className="text-sm text-[var(--color-text-secondary)] mb-4">
              Zapytaj o cokolwiek nad bazą BZP — wyszukiwanie, ceny,
              konkurencję, konkretne postępowania. EZ-ARON sam odpytuje bazę i
              odpowiada liczbami z narzędzi.
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  className="text-xs rounded-full border border-[var(--color-border)] px-3 py-1.5 hover:border-rose-400 hover:text-rose-700 dark:hover:text-rose-400 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) =>
          m.role === "user" ? (
            <div key={i} className="flex justify-end">
              <div className="max-w-[80%] rounded-2xl rounded-br-md bg-rose-600 text-white px-3.5 py-2">
                <p className="text-sm whitespace-pre-wrap">{m.content}</p>
              </div>
            </div>
          ) : (
            <div key={i} className="flex items-start gap-2.5">
              <div className="shrink-0 mt-1 h-7 w-7 rounded-full bg-gradient-to-br from-pink-500 to-rose-600 text-white flex items-center justify-center">
                <Bot size={15} />
              </div>
              <div className="min-w-0 max-w-[calc(100%-3rem)] rounded-2xl rounded-tl-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3.5 py-2.5">
                <StepsTrace msg={m} />
                <AronMarkdown text={m.content} />
                {(m.tenders?.length ?? 0) > 0 && (
                  <div className="mt-2 space-y-1.5">
                    {m.tenders!.map((t) => (
                      <TenderMiniCard key={t.id} row={t} />
                    ))}
                  </div>
                )}
                {m.cost_usd && (
                  <p className="text-[10px] text-[var(--color-text-muted)] mt-2 tabular-nums">
                    {MODELS.find((x) => x.value === m.model)?.label ?? m.model}{" "}
                    · ~${m.cost_usd}
                  </p>
                )}
              </div>
            </div>
          ),
        )}

        {ask.isPending && (
          <div className="flex items-center gap-2 text-sm text-[var(--color-text-muted)] pl-10">
            <Spinner /> EZ-ARON odpytuje bazę…
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div className="sticky bottom-0 pt-4 pb-2 bg-[var(--color-bg-base)]">
        <div className="flex items-end gap-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send(draft);
              }
            }}
            rows={2}
            placeholder="Np. „pokaż otwarte przetargi na stacje trafo powyżej 500 tys. i porównaj z cenami wygranych”…"
            className="flex-1 text-sm border border-[var(--color-border)] rounded-xl px-3 py-2 bg-transparent resize-none focus:outline-none focus:ring-2 focus:ring-rose-500/40"
          />
          <Button
            onClick={() => send(draft)}
            disabled={ask.isPending || !draft.trim()}
          >
            <Send size={14} />
          </Button>
        </div>
      </div>
    </div>
  );
}
